import { create } from 'zustand';
import pb, { Lead, Communication, PIPELINE_STAGES, PipelineStage, normalizeStatus } from './pocketbase';
import { parseDate } from './utils';

interface AppState {
  // Auth
  isAuthenticated: boolean;
  user: any | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  
  // Leads (non-paid only)
  leads: Lead[];
  selectedLead: Lead | null;
  loading: boolean;
  error: string | null;
  fetchLeads: () => Promise<void>;
  updateLeadStatus: (leadId: string, status: PipelineStage) => Promise<void>;
  updateLead: (leadId: string, data: Partial<Lead>) => Promise<void>;
  deleteLead: (leadId: string) => Promise<void>;
  selectLead: (lead: Lead | null) => void;
  
  // Clients (paid)
  clients: any[];
  fetchClients: () => Promise<void>;
  
  // Communications
  communications: Communication[];
  communicationCounts: Record<string, number>;
  fetchCommunications: (leadId: string) => Promise<void>;
  fetchAllCommunicationCounts: () => Promise<void>;
  addCommunication: (data: Omit<Communication, 'id' | 'created'>) => Promise<void>;
  
  // Stats
  stats: {
    totalLeads: number;
    byStage: Record<PipelineStage, number>;
    conversionRate: number;
    leadsThisWeek: number;
    leadsThisMonth: number;
  } | null;
  fetchStats: () => Promise<void>;
  
  // Alerts
  alerts: { lead: Lead; daysSinceContact: number }[];
  fetchAlerts: (thresholdDays?: number) => Promise<void>;
  
  // Followups
  upcomingFollowups: Lead[];
  overdueFollowups: Lead[];
  fetchFollowups: () => void;
}

export const useStore = create<AppState>((set, get) => ({
  // Auth
  isAuthenticated: !!pb.authStore.isValid,
  user: pb.authStore.model,
  
  login: async (email: string, password: string) => {
    try {
      // Try superuser auth first (PocketBase 0.20+), fallback to users collection
      try {
        await pb.collection('_superusers').authWithPassword(email, password);
      } catch {
        // Fallback to users collection if superuser auth fails
        await pb.collection('users').authWithPassword(email, password);
      }
      set({ isAuthenticated: true, user: pb.authStore.model, error: null });
    } catch (err: any) {
      set({ error: err.message || 'Login failed' });
      throw err;
    }
  },
  
  logout: () => {
    pb.authStore.clear();
    set({ isAuthenticated: false, user: null, leads: [], communications: [] });
  },
  
  // Leads (non-paid only)
  leads: [],
  selectedLead: null,
  loading: false,
  error: null,
  
  // Clients (paid)
  clients: [],
  
  fetchLeads: async () => {
    set({ loading: true, error: null });
    try {
      // Ensure auth token is loaded from localStorage before fetching
      if (!pb.authStore.isValid) {
        const stored = localStorage.getItem('pocketbase_auth');
        if (stored) {
          const data = JSON.parse(stored);
          if (data.token && data.record) {
            pb.authStore.save(data.token, data.record);
          }
        }
      }
      
      // Use getList with pagination - filter NON-PAID only (leads)
      const records = await pb.collection('submissions').getList<Lead>(1, 1000, {
        filter: "payment_status != 'paid'",
      });
      
      // Map submissions to leads format, normalizing status
      const leads = records.items.map((r: any) => ({
        ...r,
        status: normalizeStatus(r.status),
        // Map submission fields to Lead interface
        name: r.business_name || r.contact_name || 'Sans nom',
        email: r.contact_email || '',
        phone: r.contact_phone || '',
        company: r.business_name || '',
        location: r.location || '',
        activity: r.services || '',
      }));
      
      set({ leads, loading: false });
    } catch (err: any) {
      console.error('Error fetching leads:', err);
      set({ error: err.message, loading: false });
    }
  },
  
  fetchClients: async () => {
    set({ loading: true, error: null });
    try {
      if (!pb.authStore.isValid) {
        const stored = localStorage.getItem('pocketbase_auth');
        if (stored) {
          const data = JSON.parse(stored);
          if (data.token && data.record) {
            pb.authStore.save(data.token, data.record);
          }
        }
      }
      
      const records = await pb.collection('submissions').getList(1, 1000, {
        filter: "payment_status = 'paid'",
        sort: '-paid_at',
      });
      
      set({ clients: records.items as any[], loading: false });
    } catch (err: any) {
      console.error('Error fetching clients:', err);
      set({ error: err.message, loading: false });
    }
  },
  
  updateLeadStatus: async (leadId: string, status: PipelineStage) => {
    try {
      await pb.collection('submissions').update(leadId, { 
        status,
        last_contact: new Date().toISOString(),
      });
      
      set(state => ({
        leads: state.leads.map(l => 
          l.id === leadId ? { ...l, status, last_contact: new Date().toISOString() } : l
        ),
        selectedLead: state.selectedLead?.id === leadId 
          ? { ...state.selectedLead, status, last_contact: new Date().toISOString() }
          : state.selectedLead
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },
  
  updateLead: async (leadId: string, data: Partial<Lead>) => {
    try {
      await pb.collection('submissions').update(leadId, data);
      
      set(state => ({
        leads: state.leads.map(l => l.id === leadId ? { ...l, ...data } : l),
        selectedLead: state.selectedLead?.id === leadId 
          ? { ...state.selectedLead, ...data }
          : state.selectedLead
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },
  
  deleteLead: async (leadId: string) => {
    try {
      await pb.collection('submissions').delete(leadId);
      
      set(state => ({
        leads: state.leads.filter(l => l.id !== leadId),
        selectedLead: state.selectedLead?.id === leadId ? null : state.selectedLead
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },
  
  selectLead: (lead) => set({ selectedLead: lead }),
  
  // Communications
  communications: [],
  communicationCounts: {},
  
  fetchCommunications: async (leadId: string) => {
    try {
      const records = await pb.collection('communications').getFullList<Communication>({
        filter: `lead = "${leadId}"`,
        sort: '-created',
      });
      set({ communications: records });
    } catch (err: any) {
      // Collection might not exist yet
      set({ communications: [] });
    }
  },
  
  fetchAllCommunicationCounts: async () => {
    try {
      // Get all communications and count by lead
      const records = await pb.collection('communications').getFullList<Communication>({
        fields: 'lead',
      });
      
      const counts: Record<string, number> = {};
      for (const comm of records) {
        counts[comm.lead] = (counts[comm.lead] || 0) + 1;
      }
      
      set({ communicationCounts: counts });
    } catch (err: any) {
      // Collection might not exist yet
      set({ communicationCounts: {} });
    }
  },
  
  addCommunication: async (data) => {
    try {
      const record = await pb.collection('communications').create(data);
      
      // Update last_contact on the lead
      await pb.collection('submissions').update(data.lead, {
        last_contact: new Date().toISOString()
      });
      
      set(state => ({
        communications: [record as unknown as Communication, ...state.communications],
        communicationCounts: {
          ...state.communicationCounts,
          [data.lead]: (state.communicationCounts[data.lead] || 0) + 1
        },
        leads: state.leads.map(l => 
          l.id === data.lead ? { ...l, last_contact: new Date().toISOString() } : l
        )
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },
  
  // Stats
  stats: null,
  
  fetchStats: async () => {
    const { leads } = get();
    
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    const byStage = PIPELINE_STAGES.reduce((acc, stage) => {
      acc[stage.id] = leads.filter(l => normalizeStatus(l.status) === stage.id).length;
      return acc;
    }, {} as Record<PipelineStage, number>);
    
    const totalLeads = leads.length;
    const converted = byStage.converti || 0;
    const conversionRate = totalLeads > 0 ? (converted / totalLeads) * 100 : 0;
    
    const leadsThisWeek = leads.filter(l => {
      const created = parseDate(l.created);
      return created && created >= weekAgo;
    }).length;
    
    const leadsThisMonth = leads.filter(l => {
      const created = parseDate(l.created);
      return created && created >= monthAgo;
    }).length;
    
    set({
      stats: {
        totalLeads,
        byStage,
        conversionRate,
        leadsThisWeek,
        leadsThisMonth,
      }
    });
  },
  
  // Alerts
  alerts: [],
  
  fetchAlerts: async (thresholdDays = 7) => {
    const { leads } = get();
    const now = new Date();
    
    const alerts = leads
      .filter(l => {
        // Only check active leads (not converted or lost)
        if (l.status === 'converti' || l.status === 'perdu') return false;
        
        const lastContact = parseDate(l.last_contact) || parseDate(l.created);
        if (!lastContact) return false;
        
        const daysSince = Math.floor((now.getTime() - lastContact.getTime()) / (1000 * 60 * 60 * 24));
        return daysSince >= thresholdDays;
      })
      .map(lead => {
        const lastContact = parseDate(lead.last_contact) || parseDate(lead.created);
        const daysSinceContact = lastContact 
          ? Math.floor((now.getTime() - lastContact.getTime()) / (1000 * 60 * 60 * 24))
          : 999;
        return { lead, daysSinceContact };
      })
      .sort((a, b) => b.daysSinceContact - a.daysSinceContact);
    
    set({ alerts });
  },
  
  // Followups
  upcomingFollowups: [],
  overdueFollowups: [],
  
  fetchFollowups: () => {
    const { leads } = get();
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const in7Days = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    const leadsWithFollowup = leads.filter(l => {
      // Only active leads
      if (l.status === 'converti' || l.status === 'perdu') return false;
      return !!l.next_followup;
    });
    
    const overdue = leadsWithFollowup
      .filter(l => {
        const followupDate = parseDate(l.next_followup);
        return followupDate && followupDate < today;
      })
      .sort((a, b) => {
        const dateA = parseDate(a.next_followup)?.getTime() || 0;
        const dateB = parseDate(b.next_followup)?.getTime() || 0;
        return dateA - dateB;
      });
    
    const upcoming = leadsWithFollowup
      .filter(l => {
        const followupDate = parseDate(l.next_followup);
        return followupDate && followupDate >= today && followupDate <= in7Days;
      })
      .sort((a, b) => {
        const dateA = parseDate(a.next_followup)?.getTime() || 0;
        const dateB = parseDate(b.next_followup)?.getTime() || 0;
        return dateA - dateB;
      });
    
    set({ upcomingFollowups: upcoming, overdueFollowups: overdue });
  },
}));
